import Phaser from "phaser";
import { gameRenderScale } from "./renderScale";

import { EnergyMechanismScene, type EnergyVisualKind } from "./EnergyMechanismScene";

export type EnergyMechanismRuntime = {
  game: Phaser.Game;
  scene: EnergyMechanismScene;
  ready: Promise<void>;
  destroy(): void;
};

export function createEnergyMechanismGame(
  parent: HTMLElement,
  kind: EnergyVisualKind,
  reducedMotion: boolean,
  chargeRatio = 1,
): EnergyMechanismRuntime {
  const renderScale = gameRenderScale(window.devicePixelRatio || 1, document.documentElement.dataset.platform);
  const fallbackWidth = kind === "sigil" ? 42 : 140;
  const fallbackHeight = kind === "sigil" ? 48 : 174;
  const width = Math.max(fallbackWidth, parent.clientWidth || fallbackWidth);
  const height = Math.max(fallbackHeight, parent.clientHeight || fallbackHeight);
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new EnergyMechanismScene(kind, reducedMotion, chargeRatio, renderScale, resolveReady);
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(width * renderScale),
    height: Math.round(height * renderScale),
    transparent: true,
    antialias: true,
    render: { antialias: true, pixelArt: false, roundPixels: false },
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    scene: [scene],
    banner: false,
  });
  game.canvas.style.width = "100%";
  game.canvas.style.height = "100%";
  const resizeObserver = typeof ResizeObserver === "undefined"
    ? null
    : new ResizeObserver(([entry]) => {
        if (entry === undefined) return;
        const nextWidth = Math.max(fallbackWidth, Math.round(entry.contentRect.width));
        const nextHeight = Math.max(fallbackHeight, Math.round(entry.contentRect.height));
        game.scale.resize(Math.round(nextWidth * renderScale), Math.round(nextHeight * renderScale));
      });
  resizeObserver?.observe(parent);

  return {
    game,
    scene,
    ready,
    destroy: () => {
      resizeObserver?.disconnect();
      game.destroy(true);
    },
  };
}
